import type { PropsWithChildren } from 'react';
import React, { useCallback, useMemo } from 'react';

import type {
	CustomSearchExtensionPropsProviderProps,
	CustomSearchExtensionPropsContextType,
} from '@atlassian/custom-search-extension';
import { CustomSearchExtensionPropsContext } from '@atlassian/custom-search-extension';

import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import {
	EDITOR,
	getExperienceName,
	getMacroAttributesFromADFNode,
	MacroExperienceSuccess,
	RENDERER,
} from '@confluence/macro-tracker';
import { ADVANCED_SEARCH, PEOPLE_DIRECTORY } from '@confluence/named-routes';
import { useSessionData } from '@confluence/session-data';

import { useGetSpaceIdsFromSpaceKeys } from '../../link-cards/LinkCardsConfigPanel/components/DynamicContentModalDialog/useGetSpaceIdsFromSpaceKeys';
import { CustomSitesExtension } from '../../shared-components/CustomSitesExtension';
import { CustomSitesRenderedExtension } from '../../shared-components/CustomSitesRenderedExtension';

export const CustomSearchExtensionPropsProvider = ({
	children,
	mode,
	contentId = '',
	extensionNode,
}: CustomSearchExtensionPropsProviderProps) => {
	const sessionData = useSessionData();
	const getSpaceIdsFromSpaceKeys = useGetSpaceIdsFromSpaceKeys();

	const {
		cloudId = '',
		displayName = '',
		isAdminHubAIEnabled = false,
		orgId = '',
		userId = '',
	} = sessionData || {};

	const errorBoundaryComponent = useMemo(() => {
		return ({ children: childrenErrorBoundary }: PropsWithChildren<{}>) => (
			<ErrorBoundary attribution={Attribution.COMPANY_HUB}>{childrenErrorBoundary}</ErrorBoundary>
		);
	}, []);

	const experienceStartComponent = useMemo(() => {
		const Component = mode === 'editor' ? CustomSitesExtension : CustomSitesRenderedExtension;
		return ({ children: childrenExperienceStart }: PropsWithChildren<{}>) => {
			return (
				<Component
					extensionNode={extensionNode!}
					contentId={contentId}
					showNoDataState={mode === 'editor' && !extensionNode}
				>
					{childrenExperienceStart}
				</Component>
			);
		};
	}, [mode, extensionNode, contentId]);

	const experienceSuccessComponent = useCallback(() => {
		const experience = mode === 'editor' ? EDITOR : RENDERER;
		return (
			<MacroExperienceSuccess
				name={getExperienceName(experience, extensionNode!)}
				contentId={contentId}
				extensionKey={extensionNode?.extensionKey}
				mode={experience}
				attributes={getMacroAttributesFromADFNode(extensionNode!)}
			/>
		);
	}, [extensionNode, contentId, mode]);

	const props: CustomSearchExtensionPropsContextType = useMemo(
		() => ({
			mode,
			orgId,
			cloudId,
			isAdminHubAIEnabled,
			userId: userId || '',
			getSpaceIdsFromSpaceKeys,
			displayName: displayName || '',
			advancedSearchUrl: ADVANCED_SEARCH.toUrl(),
			peopleDirectoryUrl: PEOPLE_DIRECTORY.toUrl(),
			errorBoundaryComponent,
			experienceStartComponent,
			experienceSuccessComponent,
		}),
		[
			mode,
			orgId,
			userId,
			cloudId,
			displayName,
			isAdminHubAIEnabled,
			getSpaceIdsFromSpaceKeys,
			errorBoundaryComponent,
			experienceStartComponent,
			experienceSuccessComponent,
		],
	);

	return (
		<CustomSearchExtensionPropsContext.Provider value={props}>
			{children}
		</CustomSearchExtensionPropsContext.Provider>
	);
};
